import { BehaviorSubject, map, Subject } from 'rxjs';
import { simpleAbilityCheck, simpleAbilityCheckStrict } from '../ability';
import { SessionUtilsShared } from './session-utils';
import { roleCheck, UserRole } from './user-role';
/**
 * OnSessionInitialized is fired frequently as session is loaded on every request.
 * OnSessionResumed fires less often, it indicates a frontend session was started using an existing session.
 *   in frontend, both are very similar because the session doesn't need to be initialized per request.
 */
export class OnSessionInitialized {
  constructor(sessionInfo) {
    this.sessionInfo = sessionInfo;
  }
}
export class OnSessionResumed {
  constructor(sessionInfo) {
    this.sessionInfo = sessionInfo;
  }
}
export class OnSessionEnd {
  constructor() {}
}
export class OnSessionReloaded {
  constructor(sessionInfo) {
    this.sessionInfo = sessionInfo;
  }
}
export class OnInvalidSessionId {
  constructor() {}
}
export var AccessServiceState = /*#__PURE__*/function (AccessServiceState) {
  AccessServiceState[AccessServiceState["INITIALIZING"] = 0] = "INITIALIZING";
  AccessServiceState[AccessServiceState["READY"] = 1] = "READY";
  AccessServiceState[AccessServiceState["RELOADING"] = 2] = "RELOADING";
  return AccessServiceState;
}(AccessServiceState || {});
export class BaseAccessService {
  constructor() {
    // WARNING: remember to correctly unsubscribe when using events in backend!
    this.events = new Subject();
    this.status$ = new BehaviorSubject(AccessServiceState.INITIALIZING);
    // NOTE: sessionInfo is populated by platform-specific child AccessServices!
    this._sessionInfo$ = new BehaviorSubject(null);
    this.programmaticAbilities = {};
    // only available in DEVELOPMENT MODE - for validating action IDs - injected by child constructors
    this.abilitySubjectsRegistry = null;
    this.accessServicePlugins = null;
  }
  get status() {
    return this.status$.getValue();
  }
  get sessionInfo() {
    return this._sessionInfo$.value;
  }
  get sessionInfo$() {
    return this._sessionInfo$.asObservable();
  }
  get role() {
    return this._sessionInfo$.value?.role || UserRole.GUEST;
  }
  get abilities() {
    return {
      ...(this._sessionInfo$.value?.abilities || {}),
      ...this.programmaticAbilities
    };
  }
  // TODO: rename to hasAuthInfo to make it clearer
  get isLoggedIn() {
    return Boolean(this.sessionInfo?.sessionState && SessionUtilsShared.hasAuthInfo(this.sessionInfo.sessionState));
  }
  get isRegistered() {
    return this.hasRole('REGISTERED');
  }
  get isAdmin() {
    return this.hasRole('ADMINISTRATOR');
  }
  get isSU() {
    return this.hasRole('SUPERUSER');
  }
  get activatedFeatures$() {
    return this._sessionInfo$.pipe(map(info => info?.activatedFeatures || []));
  }
  get activatedFeatures() {
    return this._sessionInfo$.value?.activatedFeatures || [];
  }
  setupPlugins(pluginCtors, injector) {
    if (!pluginCtors?.length) throw new Error('BaseAccessService error: no AccessServicePlugins were found. Aborting setup.');
    this.accessServicePlugins = pluginCtors.map(ctor => {
      const plugin = new ctor(injector);
      if (plugin.initialize) plugin.initialize(this);
      return plugin;
    });
  }
  setProgrammaticAbility(name, bias) {
    if (bias === null && this.programmaticAbilities[name]) {
      delete this.programmaticAbilities[name];
    } else {
      this.programmaticAbilities[name] = bias;
    }
  }
  // "facade" exposing some contents of AccessServicePlugin 
  getAccessData(name, extraParam = null, handleUnknown = 'null') {
    const accessorInfo = this.findDataAccessor(name, handleUnknown);
    if (accessorInfo === null) return null;
    return this.executeDataAccessor(accessorInfo.plugin, accessorInfo.accessor, this._sessionInfo$.value, extraParam);
  }
  getAccessData$(name, extraParam = null, handleUnknown = 'null') {
    const accessorInfo = this.findDataAccessor(name, handleUnknown);
    if (accessorInfo === null) return null;
    return this._sessionInfo$.pipe(map(info => {
      return this.executeDataAccessor(accessorInfo.plugin, accessorInfo.accessor, info, extraParam);
    }));
  }
  executeDataAccessor(plugin, accessor, sessionInfo, extraParam) {
    try {
      if (!sessionInfo) return null;
      const getPluginData = pluginId => {
        if (!pluginId) pluginId = plugin.pluginId;
        // in case data of the requested plugin is unavailable, return null.
        if (!sessionInfo.accessData[pluginId]) return null;
        return sessionInfo.accessData[pluginId];
      };
      return accessor(getPluginData, sessionInfo, extraParam);
    } catch (err) {
      /**
       * without catching, it may look like errors happen somewhere inside component templates.
       * to keep application intact, we catch errors, log a warning and return null instead.
       */
      console.warn('Error while executing DataAccessor: ' + err);
      return null;
    }
  }
  findDataAccessor(name, handleUnknown = 'null') {
    for (const plugin of this.accessServicePlugins) {
      if (plugin.dataAccessors[name]) {
        return {
          plugin,
          accessor: plugin.dataAccessors[name]
        };
      }
    }
    if (handleUnknown === 'null') {
      if (!IS_PRODUCTION) console.warn('unknown accessData accessor: ' + name);
      return null;
    } else {
      throw new Error('AccessServicePlugin has no dataAccessor for data with name "' + name + '"');
    }
  }
  assertPluginIsReady() {
    return Promise.resolve(true);
  }
  getAuthInfo() {
    const sess = this._sessionInfo$.value?.sessionState;
    if (!sess) return null;
    return {
      authType: sess.auth.authType,
      authId: sess.auth.authId
    };
  }
  getSessionScope() {
    const session = this._sessionInfo$.value?.sessionState;
    return session?.sessionScope || null;
  }
  checkSessionScope(requiredScope) {
    return this.getSessionScope() === requiredScope;
  }
  startWithoutSession() {
    this._sessionInfo$.next(null);
    this.events.next(new OnSessionInitialized(null));
    return Promise.resolve(true);
  }
  endSession() {
    if (!this._sessionInfo$.getValue() && this.status === AccessServiceState.READY) return true;
    // possibly called multiple times, so check if a session is actually there to end.
    this.status$.next(AccessServiceState.READY);
    this.events.next(new OnSessionEnd());
    this._sessionInfo$.next(null);
    return Promise.resolve(true);
  }
  hasRole(...roles) {
    for (const role of roles) {
      if (roleCheck(role, this.role)) return true;
    }
    return false;
  }
  simpleAbilityCheck(requirement, respectSuperuser = true, strict = false) {
    if (strict) return simpleAbilityCheckStrict(this.role, this.abilities, requirement, respectSuperuser);else return simpleAbilityCheck(this.role, this.abilities, requirement, respectSuperuser);
  }
  // custom destroy mechanism.
  // can be ignored in frontend, where AccessService is a singleton.
  // in backend, AccessMiddleware triggers it on response finish.
  // this is important to allow AccessService to clean up its observables & subscribers.
  destroy() {}
}